/*  Shoppable-slideshow-component  */ 
if (!customElements.get('shoppable-slideshow-component')) {
  customElements.define('shoppable-slideshow-component', class extends HTMLElement {
    constructor() {
      super();
      if (this.dataset.enableSlider !== 'true') return;

      this.slidesDesktop = 5;
      this.slidesMobile  = 1.7;
      this.autoplayDelay = 3000; // 3s
      this.isTransitioning = false;
      this.initSlider();
    }

    initSlider() {
      const options = {
        slidesPerGroup: 1,
        loop: true,
        grabCursor: true,
        threshold: 2,
        centeredSlides: true,
        breakpoints: {
          0:   { slidesPerView: this.slidesMobile,  spaceBetween: 10 },
          768: { slidesPerView: this.slidesDesktop, spaceBetween: 15 }
        },
        autoplay: { delay: this.autoplayDelay, disableOnInteraction: false },
        pagination: {
          el: this.querySelector('.swiper-pagination'),
          clickable: true
        },
        navigation: {
          nextEl: this.querySelector('.swiper-button-next'),
          prevEl: this.querySelector('.swiper-button-prev')
        },
        on: {
          init: (swiper) => {
            this.setupCustomControls(swiper);
            this.updateVideoStates(swiper);
          },
          slideChangeTransitionStart: (swiper) => {
            this.isTransitioning = true;
            this.pauseAllVideos(swiper);
          },
          slideChangeTransitionEnd: (swiper) => {
            this.isTransitioning = false;
            this.updateVideoStates(swiper);
          }
        }
      };

      this.sliderInstance = new window.FoxTheme.Carousel(
        this,
        options,
        [
          FoxTheme.Swiper.Autoplay,
          FoxTheme.Swiper.Pagination,
          FoxTheme.Swiper.Navigation
        ]
      );

      this.sliderInstance.init();
    }

    setupCustomControls(swiper) {
      // Setup play button handlers
      this.querySelectorAll('.video-play-overlay').forEach(playBtn => {
        playBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          const videoContainer = playBtn.closest('.video-item-inner');
          const video = videoContainer.querySelector('.media-video');
          const slide = playBtn.closest('.swiper-slide');
          this.handlePlayClick(video, slide, swiper);
        });
      });

      // Setup sound button handlers
      this.querySelectorAll('.video-sound-overlay').forEach(soundBtn => {
        soundBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          const videoContainer = soundBtn.closest('.video-item-inner');
          const video = videoContainer.querySelector('.media-video');
          const slide = soundBtn.closest('.swiper-slide');
          this.handleSoundClick(video, slide, swiper);
        });
      });

      // Monitor video play/pause states
      this.querySelectorAll('.media-video').forEach(video => {
        video.addEventListener('play', () => {
          const container = video.closest('.video-item-inner');
          container.classList.add('is-playing');
        });

        video.addEventListener('pause', () => {
          const container = video.closest('.video-item-inner');
          container.classList.remove('is-playing');
        });

        video.addEventListener('ended', () => {
          const container = video.closest('.video-item-inner');
          container.classList.remove('is-playing');
          video.currentTime = 0;
          
          // Move to next slide and restart autoplay
          swiper.slideNext();
          setTimeout(() => {
            if (!this.isTransitioning) {
              swiper.autoplay.start();
            }
          }, 100);
        });

        video.addEventListener('timeupdate', () => {
          const slide = video.closest('.swiper-slide');
          const bar = slide.querySelector('.progress-bar');
          if (bar && video.duration) {
            bar.style.width = (video.currentTime / video.duration) * 100 + '%';
          }
        });
      });
    }

    handlePlayClick(video, slide, swiper) {
      if (this.isTransitioning) return;

      const isActiveSlide = slide.classList.contains('swiper-slide-active');

      // Stop autoplay
      swiper.autoplay.stop();

      // Pause all other videos
      this.pauseAllVideos(swiper);

      if (!isActiveSlide) {
        // Get real index and slide to it
        const realIndex = this.getRealSlideIndex(slide);
        if (realIndex !== -1) {
          // Slide to the video
          swiper.slideToLoop(realIndex, 300);
          
          // Play after transition completes
          setTimeout(() => {
            if (!this.isTransitioning) {
              const activeSlide = swiper.slides[swiper.activeIndex];
              const activeVideo = activeSlide ? activeSlide.querySelector('.media-video') : null;
              if (activeVideo) {
                activeVideo.play().catch(err => console.log('Play prevented:', err));
              }
            }
          }, 350);
        }
      } else {
        // Already centered, just play
        video.play().catch(err => console.log('Play prevented:', err));
      }
    }

    handleSoundClick(video, slide, swiper) {
      if (this.isTransitioning) return;

      const isActiveSlide = slide.classList.contains('swiper-slide-active');
      const videoContainer = video.closest('.video-item-inner');
      const currentlyMuted = video.muted;

      if (!isActiveSlide) {
        // Stop autoplay
        swiper.autoplay.stop();

        // Pause all other videos
        this.pauseAllVideos(swiper);

        // Get real index and slide to it
        const realIndex = this.getRealSlideIndex(slide);
        if (realIndex !== -1) {
          // Slide to the video
          swiper.slideToLoop(realIndex, 300);
          
          // Toggle sound and play after transition completes
          setTimeout(() => {
            if (!this.isTransitioning) {
              const activeSlide = swiper.slides[swiper.activeIndex];
              const activeVideo = activeSlide ? activeSlide.querySelector('.media-video') : null;
              const activeContainer = activeSlide ? activeSlide.querySelector('.video-item-inner') : null;
              
              if (activeVideo && activeContainer) {
                // Toggle mute state
                activeVideo.muted = !currentlyMuted;
                if (activeVideo.muted) {
                  activeContainer.classList.remove('is-unmuted');
                } else {
                  activeContainer.classList.add('is-unmuted');
                }
                
                // Play the video
                if (activeVideo.paused) {
                  activeVideo.play().catch(err => console.log('Play prevented:', err));
                }
              }
            }
          }, 350);
        }
      } else {
        // Already centered, just toggle mute
        video.muted = !video.muted;
        
        if (video.muted) {
          videoContainer.classList.remove('is-unmuted');
        } else {
          videoContainer.classList.add('is-unmuted');
          // Stop autoplay when unmuting
          swiper.autoplay.stop();
        }
        
        // If video is paused, play it
        if (video.paused) {
          video.play().catch(err => console.log('Play prevented:', err));
        }
      }
    }

    getRealSlideIndex(slide) {
      const swiper = this.sliderInstance.slider;
      
      // For loop mode, get the real index from data attribute
      if (swiper.params.loop) {
        const dataIndex = slide.getAttribute('data-swiper-slide-index');
        if (dataIndex !== null) {
          return parseInt(dataIndex);
        }
      }
      
      // Fallback: find the index in the slides array
      return swiper.slides.indexOf(slide);
    }

    pauseAllVideos(swiper) {
      swiper.slides.forEach(slide => {
        const video = slide.querySelector('.media-video');
        const videoContainer = slide.querySelector('.video-item-inner');
        const bar = slide.querySelector('.progress-bar');
        
        if (video) {
          if (!video.paused) {
            video.pause();
          }
          video.currentTime = 0;
          video.muted = true;
          
          if (videoContainer) {
            videoContainer.classList.remove('is-playing');
            videoContainer.classList.remove('is-unmuted');
          }
          
          if (bar) {
            bar.style.width = '0%';
          }
        }
      });
    }

    updateVideoStates(swiper) {
      if (!swiper || this.isTransitioning) return;

      const slides = swiper.slides;
      const activeSlide = slides[swiper.activeIndex];

      // Update all video containers and autoplay active video
      slides.forEach(slide => {
        const videoContainer = slide.querySelector('.video-item-inner');
        const video = slide.querySelector('.media-video');
        
        if (videoContainer && video) {
          if (slide === activeSlide) {
            // This is the active/centered slide - autoplay it
            if (video.paused) {
              swiper.autoplay.stop();
              videoContainer.classList.add('is-playing');
              video.play().catch(err => console.log('Autoplay prevented:', err));
            }
          } else {
            videoContainer.classList.remove('is-playing');
          }
        }
      });
    }
  });
}
